import type { AutosaveStatus } from '../../components/AutosaveIndicator';
import type { RoadmapSaveStatus } from '../../components/edit-object/design/Sidebar';

interface ThumbCandidate {
  imageId: string;
  thumbnailUrl?: string;
}

const SAVE_STATUS_LABEL: Record<AutosaveStatus, string> = {
  idle: 'No changes',
  saving: 'Saving…',
  saved: 'All changes saved',
  error: 'Save failed',
  dirty: 'Unsaved changes',
};

/**
 * Pick the thumbnail URL to display in the sidebar artwork header.
 *
 * Order of preference:
 *   1. The cover image (`selectedCoverId`) if it has a thumbnail.
 *   2. The first uploaded image's thumbnail.
 *   3. `null` (no image yet).
 */
export const derivePrimaryThumbUrl = (
  uploadedImages: readonly ThumbCandidate[],
  selectedCoverId: string | null,
): string | null => {
  if (uploadedImages.length === 0) return null;
  const cover = selectedCoverId
    ? uploadedImages.find((img) => img.imageId === selectedCoverId)
    : null;
  return cover?.thumbnailUrl ?? uploadedImages[0]?.thumbnailUrl ?? null;
};

/**
 * Map the wizard's autosave state to the sidebar's save-status pill props.
 * Returns 'saved' as a default — callers gate visibility separately via
 * `shouldShowSidebarSaveStatus` to suppress the pill until a real save
 * has been attempted.
 */
export const computeSidebarSaveStatus = (
  status: AutosaveStatus,
  lastSaveTime?: Date,
): RoadmapSaveStatus => {
  if (status === 'saving') return { state: 'saving', label: SAVE_STATUS_LABEL.saving };
  if (status === 'error') return { state: 'error', label: SAVE_STATUS_LABEL.error };
  // 'dirty' = unsaved edits with a save pending (2s debounce). RoadmapSaveStatus has
  // no 'dirty' state, so reuse the 'saving' visual (a save is imminent) — never show
  // "All changes saved" while edits are pending (GL#40 Part 1).
  if (status === 'dirty') return { state: 'saving', label: SAVE_STATUS_LABEL.dirty };
  const timestamp = lastSaveTime?.toLocaleTimeString();
  const label = timestamp ? `${SAVE_STATUS_LABEL.saved} · ${timestamp}` : SAVE_STATUS_LABEL.saved;
  return { state: 'saved', label };
};

/**
 * The save-status footer is hidden until the wizard has actually attempted
 * a save — i.e. until the autosave state has left 'idle' OR a save
 * timestamp has been recorded. This avoids showing "All changes saved" on
 * a brand-new object before anything has been entered.
 */
export const shouldShowSidebarSaveStatus = (
  status: AutosaveStatus,
  lastSaveTime?: Date,
): boolean => status !== 'idle' || lastSaveTime !== undefined;
